import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ShortcutsHelpModal, type ShortcutItem } from "./shortcuts-help-modal";

export type { ShortcutItem };

type Registration = {
  id: string;
  title: string;
  shortcuts: ShortcutItem[];
};

type ShortcutsContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  register: (id: string, title: string, shortcuts: ShortcutItem[]) => void;
  unregister: (id: string) => void;
  hasPageShortcuts: boolean;
};

const ShortcutsContext = createContext<ShortcutsContextValue | null>(null);

const DEFAULT_TITLE = "Keyboard shortcuts";
const GLOBAL_SHORTCUTS: ShortcutItem[] = [
  {
    keys: ["?"],
    label: "This help",
    description: "Open or close this shortcuts overlay from anywhere in the app.",
    enabled: true,
  },
];

/**
 * Task #31 — global keyboard-shortcuts provider. Pages register a
 * page-specific shortcut list via `useRegisterShortcuts`; the most-recently
 * registered page wins (stack semantics), which matches how operators
 * navigate (current page = top of stack). The provider owns the "?" hotkey
 * and the modal, so individual pages no longer wire their own.
 */
export function ShortcutsProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  // We keep registrations as an ordered list — latest push is rendered.
  // Using a ref + state so re-registers (e.g. when a page recomputes its
  // shortcut list because permissions resolved) don't lose ordering.
  const [stack, setStack] = useState<Registration[]>([]);

  const register = useCallback((id: string, title: string, shortcuts: ShortcutItem[]) => {
    setStack((prev) => {
      const without = prev.filter((r) => r.id !== id);
      return [...without, { id, title, shortcuts }];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setStack((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Global "?" hotkey. Ignored while typing in inputs or with modifier
  // combos so we don't stomp on Ctrl+? or similar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "?") return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const typing =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target?.isContentEditable;
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const top = stack.length > 0 ? stack[stack.length - 1]! : null;
  const title = top?.title ?? DEFAULT_TITLE;
  // Show the page's shortcuts followed by the always-on globals (deduped
  // by label so a page can override the "?" entry if it wants).
  const pageShortcuts = top?.shortcuts ?? [];
  const merged = useMemo<ShortcutItem[]>(() => {
    const seen = new Set(pageShortcuts.map((s) => s.label.toLowerCase()));
    const extras = GLOBAL_SHORTCUTS.filter((g) => !seen.has(g.label.toLowerCase()));
    return [...pageShortcuts, ...extras];
  }, [pageShortcuts]);

  const value = useMemo<ShortcutsContextValue>(
    () => ({
      open,
      setOpen,
      toggle,
      register,
      unregister,
      hasPageShortcuts: pageShortcuts.length > 0,
    }),
    [open, toggle, register, unregister, pageShortcuts.length],
  );

  return (
    <ShortcutsContext.Provider value={value}>
      {children}
      <ShortcutsHelpModal
        open={open}
        onOpenChange={setOpen}
        title={title}
        shortcuts={merged}
      />
    </ShortcutsContext.Provider>
  );
}

export function useShortcuts(): ShortcutsContextValue {
  const ctx = useContext(ShortcutsContext);
  if (!ctx) {
    throw new Error("useShortcuts must be used inside <ShortcutsProvider>");
  }
  return ctx;
}

/**
 * Register the current page's shortcut list. The most-recently mounted
 * caller wins (stack); on unmount, the registration is removed and the
 * previous page's shortcuts take over.
 *
 * Pass a stable `shortcuts` array (e.g. via `useMemo`) — the registration
 * re-runs whenever the array identity changes, which is the right
 * behaviour for permission/state-driven enable flags.
 */
/**
 * Task #37 — bind a real keydown handler for a single page-level hotkey.
 * Mirrors the typing-guard pattern from the global "?" handler in
 * `ShortcutsProvider` so the hotkey is ignored while the operator is
 * typing in an input/textarea/select/contenteditable or holds a modifier.
 *
 * `key` is matched case-sensitively against `KeyboardEvent.key` after
 * lowercasing both sides (so "N" and "n" both bind to the same letter
 * without us having to register two handlers). Single-character keys
 * only — for "?", use the provider's built-in handler.
 *
 * Pass `enabled: false` to no-op (e.g. when the operator lacks the
 * permission for the action).
 */
export function usePageHotkey(
  key: string,
  handler: (e: KeyboardEvent) => void,
  opts?: { enabled?: boolean },
): void {
  const enabled = opts?.enabled ?? true;
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  useEffect(() => {
    if (!enabled) return;
    const wanted = key.toLowerCase();
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== wanted) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const typing =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target?.isContentEditable;
      if (typing) return;
      handlerRef.current(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [key, enabled]);
}

export function useRegisterShortcuts(title: string, shortcuts: ShortcutItem[]): void {
  const ctx = useContext(ShortcutsContext);
  const generatedId = useId();
  const idRef = useRef(generatedId);
  useEffect(() => {
    if (!ctx) return;
    const id = idRef.current;
    ctx.register(id, title, shortcuts);
    return () => ctx.unregister(id);
    // ctx.register/unregister are stable; re-register when title or
    // shortcuts identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, shortcuts]);
}
